import { type ReactNode, forwardRef } from 'react';

import {
  BaseInfiniteScroll,
  type IBaseInfiniteScrollProps,
} from '../BaseInfiniteScroll';
import { type IDisplayContentProps } from '../DisplayContent';
import {
  GroupContent,
  HeaderItemGrid,
  MainContent,
  SortedDownActiveIcon,
  SortedDownIcon,
  SortedUpActiveIcon,
} from './styles';

export interface IColumn {
  id: string;
  name: string;
  textAlign?: string;
  allowSort?: boolean;
}

export interface ISort {
  id: string;
  direction: 'ASC' | 'DESC';
}

export interface ITableProps extends Partial<IBaseInfiniteScrollProps> {
  children?: ReactNode;
  columns: IColumn[];
  loading?: boolean;
  dataLength: number;
  next: () => void;
  hasMore: boolean;
  endMessage?: string;
  noItensMessage?: string;
  themeStyle?: 'admin' | 'default';
  tableStyles?: React.CSSProperties;
  onClickHeader?: (column: IColumn) => void;
  columnSorted?: ISort | null;
}

export const Table = forwardRef<HTMLDivElement, ITableProps>(
  (
    {
      children,
      columns,
      loading = false,
      dataLength,
      next,
      hasMore,
      endMessage = 'You have seen it all',
      noItensMessage = 'No items found',
      themeStyle = 'default',
      tableStyles,
      onClickHeader,
      columnSorted,
      ...props
    },
    ref
  ): JSX.Element => {
    return (
      <BaseInfiniteScroll
        {...props}
        dataLength={dataLength}
        next={next}
        hasMore={hasMore}
        loading={loading}
        endMessage={dataLength ? endMessage : noItensMessage}
      >
        <MainContent
          ref={ref}
          data-cy="table-container"
          $columns={columns.length}
          $themeStyle={themeStyle}
          style={tableStyles}
        >
          {columns.map((column) => (
            <HeaderItemGrid
              data-cy={column.id}
              key={column.id}
              style={{
                padding:
                  column.name === ''
                    ? '2rem 2rem 0.75rem'
                    : '0.5rem 0.5rem 0.75rem',
                justifyContent: (column.textAlign ?? 'center') as
                  | 'center'
                  | 'end'
                  | 'start',
                textAlign: (column.textAlign ?? 'center') as
                  | 'center'
                  | 'end'
                  | 'start',
                cursor: column.allowSort === true ? 'pointer' : 'default',
              }}
              onClick={() => onClickHeader?.(column)}
            >
              <p data-cy={`column-${column.name.toLowerCase()}`}>
                {column.name}
              </p>

              {columnSorted?.id === column.id ? (
                <>
                  {columnSorted.direction === 'ASC' && <SortedUpActiveIcon />}
                  {columnSorted.direction === 'DESC' && (
                    <SortedDownActiveIcon />
                  )}
                </>
              ) : (
                column.allowSort === true && <SortedDownIcon />
              )}
            </HeaderItemGrid>
          ))}
          {children}
        </MainContent>
      </BaseInfiniteScroll>
    );
  }
);

Table.displayName = 'Table';

export interface ITableRowProps extends IDisplayContentProps {
  cursor?: string;
  selected?: boolean;
}

export const TableRow = forwardRef<HTMLDivElement, ITableRowProps>(
  // eslint-disable-next-line no-restricted-syntax
  function TableRow({ cursor = 'pointer', selected, ...props }, ref): JSX.Element {
    return <GroupContent ref={ref} {...props} $cursor={cursor} $selected={selected} />;
  }
);
